import { faCheck, faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Stack } from "react-bootstrap";
import { deleteJob, fetchJobs, patchJob } from "../../../services/httpClient";
import Input from "../../ui/Input";
import { unsavedChanges } from "../../../App";
import IconButton from "../../ui/IconButton";

interface CardState {
  item: any;
  getItems: any;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const JobCard = ({ item, getItems, setOrgToSave, setErrMessage, setShowToast, setToastText, setToastBody }: CardState) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemName, setItemName] = useState<string>(item?.name);
  const [itemDept, setItemDept] = useState<string>(item?.department);
  const [prevDept, setPrevDept] = useState<string>(item?.department);
  const [positionsTotal, setPositionsTotal] = useState<string>(item?.positions_total);

  useEffect(() => {
    setItemName(item?.name);
    setItemDept(item?.department);
    setPrevDept(item?.department);
    setPositionsTotal(item?.positions_total);
  }, [item]);

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const onClickEditFinal = async () => {
    if (item?.positions_filled > positionsTotal) {
      setErrMessage("Cannot Set Positions Total Less Than Positions Filled");
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
      return;
    }

    const updatedJob = {
      department: itemDept,
      name: itemName,
      positions_total: positionsTotal,
    };

    await patchJob(updatedJob, item?.id)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Job Updated");
        setToastBody(itemName);
        setIsEditing(false);
        return updateDepartments();
      })
      .then(() => {
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const updateDepartments = async () => {
    return fetchJobs()
      .then(async (res: any) => {
        var jobs = res[0].data;
        jobs = jobs.filter((j: any) => j.department === prevDept);

        await Promise.all(
          jobs.map((j: any) => {
            const updatedJob = {
              department: itemDept,
            };

            return patchJob(updatedJob, j?.id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMessage(err?.response?.data?.msg);
                window.scrollTo({
                  top: 0,
                  behavior: "auto",
                });
              });
          })
        );
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickCancelEdit = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsEditing(false);
    setErrMessage(undefined);
  };

  const onUpdateDept = (e: any) => {
    setItemDept(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onUpdateName = (e: any) => {
    setItemName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onUpdateTotal = (e: any) => {
    setPositionsTotal(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    deleteJob(item?.id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Job Deleted");
        setToastBody(itemName);
        setIsDeleting(false);
        getItems(true);
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setErrMessage(undefined);
  };

  const renderEdit = () => {
    return (
      <Stack direction="horizontal" gap={3}>
        <Col xs={4}>
          <Input label="Department" type="text" value={itemDept} onUpdate={(e: any) => onUpdateDept(e)} />
        </Col>
        <Col xs={4}>
          <Input label="Name" type="text" value={itemName} onUpdate={(e: any) => onUpdateName(e)} />
        </Col>
        <Col xs={2}>
          <Input label="Positions" type="number" value={positionsTotal} onUpdate={(e: any) => onUpdateTotal(e)} />
        </Col>
        <Stack className="ms-auto" direction="horizontal" gap={3}>
          <IconButton onClickButton={onClickEditFinal} tooltipText={"Save"} icon={faCheck} />
          <div className="vr" />
          <IconButton onClickButton={onClickCancelEdit} tooltipText={"Cancel"} icon={faClose} />
        </Stack>
      </Stack>
    );
  };

  const renderDelete = () => {
    return (
      <Stack direction="horizontal" gap={3}>
        <Stack style={{ justifyContent: "center" }} gap={2}>
          <Card.Subtitle>Remove {item?.name}?</Card.Subtitle>
        </Stack>
        <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
          Confirm Delete
        </Button>
        <div className="vr" />
        <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
          Cancel
        </Button>
      </Stack>
    );
  };

  return (
    <Card key={item.name} className="mb-2">
      <Card.Body>
        {isEditing ? (
          <>{renderEdit()}</>
        ) : isDeleting ? (
          <>{renderDelete()}</>
        ) : (
          <Stack direction="horizontal" gap={3}>
            <Stack gap={2} className="align-self-center">
              <Card.Subtitle className="text-muted">
                <small>{item.name}</small>
              </Card.Subtitle>
            </Stack>
            <small>
              Positions: {item.positions_filled}/{item.positions_total}
            </small>
            <IconButton onClickButton={onClickEdit} tooltipText={"Edit"} icon={faEdit} />

            <div className="vr" />
            {item.positions_filled > 0 ? (
              <IconButton tooltipText={"Must Unassign Jobs"} icon={faTrash} />
            ) : (
              <IconButton onClickButton={onClickDelete} tooltipText={"Delete"} icon={faTrash} />
            )}
          </Stack>
        )}
      </Card.Body>
    </Card>
  );
};

export default JobCard;
